<template>
  <div class="settings-page">
    <h2>Permissions</h2>
    <div class="subtitle">Manage permissions and device preferences.</div>

    <div class="permissions">
      <div class="permission">
        <div class="header">
          <div class="content">
            <h3>Microphone</h3>
            <p>Required for voice input and audio recording features.</p>
          </div>
          <div class="actions">
            <span :class="['status', microphonePermissionStatus]">
              {{ formatPermissionStatus(microphonePermissionStatus) }}
            </span>
            <BaseButton
              v-if="microphonePermissionStatus !== 'granted'"
              kind="main"
              type="secondary"
              text="Request"
              @click="requestMicrophonePermission"
            />
          </div>
        </div>
        <div class="device-select">
          <VSelect
            label="label"
            :clearable="false"
            :searchable="true"
            :reduce="(device: MicrophoneDevice) => device.deviceId"
            :options="microphoneDevices"
            :model-value="selectedMicrophoneDeviceId"
            :disabled="microphoneDevices.length === 0"
            class="select"
            @update:model-value="onMicrophoneDeviceChange"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
          <p
            v-if="
              microphoneDevices.length === 0 &&
              microphonePermissionStatus !== 'granted'
            "
            class="help"
          >
            Grant permission to select a device.
          </p>
          <p
            v-else-if="
              microphoneDevices.length === 0 &&
              microphonePermissionStatus === 'granted'
            "
            class="help"
          >
            No microphones found.
          </p>
        </div>
      </div>

      <div class="permission">
        <div class="header">
          <div class="content">
            <h3>Speakers</h3>
            <p>Select your preferred audio output device.</p>
          </div>
          <span :class="['status', speakerPermissionStatus]">
            {{ formatPermissionStatus(speakerPermissionStatus) }}
          </span>
        </div>
        <div class="device-select">
          <VSelect
            label="label"
            :clearable="false"
            :searchable="true"
            :reduce="(device: SpeakerDevice) => device.deviceId"
            :options="speakerDevices"
            :model-value="selectedSpeakerDeviceId"
            :disabled="speakerDevices.length === 0"
            class="select"
            @update:model-value="onSpeakerDeviceChange"
          >
            <template #open-indicator="{ attributes }">
              <span v-bind="attributes"><ChevronDownIcon /></span>
            </template>
          </VSelect>
          <p
            v-if="
              speakerDevices.length === 0 &&
              microphonePermissionStatus !== 'granted'
            "
            class="help"
          >
            Grant microphone permission to see available speakers.
          </p>
          <p v-else-if="speakerDevices.length === 0" class="help">
            No speakers found.
          </p>
        </div>
      </div>

      <div class="permission">
        <div class="header">
          <div class="content">
            <h3>Screen Recording</h3>
            <p v-if="in_electron && in_mac()">
              Required for capturing other people's voices in meetings.
            </p>
            <p v-else-if="!in_electron">
              Permission is requested when capturing system audio is needed.
            </p>
            <p v-else>Screen recording permission for audio capture.</p>
          </div>
          <span :class="['status', screenRecordingPermissionStatus]">
            {{ formatPermissionStatus(screenRecordingPermissionStatus) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import VSelect from "vue-select";

import BaseButton from "@/components/base/BaseButton.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";
import { in_electron, in_mac } from "@/utils/in";
import { microphone, type MicrophoneDevice } from "@/utils/microphone";
import type { PeripheralPermissionStatus } from "@/utils/peripherals";
import { screenRecording } from "@/utils/screen-recording";
import { speaker, type SpeakerDevice } from "@/utils/speakers";

export default defineComponent({
  components: {
    BaseButton,
    ChevronDownIcon,
    VSelect,
  },
  setup() {
    const profile = inject.profile();
    const microphonePermissionStatus =
      ref<PeripheralPermissionStatus>("unknown");
    const screenRecordingPermissionStatus =
      ref<PeripheralPermissionStatus>("unknown");
    const speakerPermissionStatus = ref<PeripheralPermissionStatus>("unknown");
    const microphoneDevices = ref<MicrophoneDevice[]>([]);
    const selectedMicrophoneDeviceId = ref<string | null>(null);
    const speakerDevices = ref<SpeakerDevice[]>([]);
    const selectedSpeakerDeviceId = ref<string | null>(null);

    const formatPermissionStatus = (
      status: PeripheralPermissionStatus
    ): string => {
      switch (status) {
        case "granted":
          return "Granted";
        case "denied":
          return "Denied";
        case "not-determined":
          return "Not Determined";
        case "unknown":
          return "Unknown";
        case "unsupported":
          return "Not Supported";
        default:
          return "Unknown";
      }
    };

    const refreshPermissions = async () => {
      microphonePermissionStatus.value = await microphone.getPermission();
      speakerPermissionStatus.value = await speaker.getPermission();
      screenRecordingPermissionStatus.value =
        await screenRecording.getPermission();
    };

    const refreshDevices = async () => {
      // Only enumerate devices if permission is already granted
      // Device labels are only available after permission is granted
      if (microphonePermissionStatus.value === "granted") {
        microphoneDevices.value = await microphone.enumerateDevices();
      } else {
        microphoneDevices.value = [];
      }

      // Load stored preferences
      const storedMicrophoneId = microphone.getSelectedDeviceId(
        profile.value.user.accountId
      );
      if (storedMicrophoneId) {
        // Verify the device still exists
        if (
          microphoneDevices.value.some((d) => d.deviceId === storedMicrophoneId)
        ) {
          selectedMicrophoneDeviceId.value = storedMicrophoneId;
        }
      } else if (microphoneDevices.value.length > 0) {
        // Use default if no preference stored
        selectedMicrophoneDeviceId.value = microphoneDevices.value[0].deviceId;
      }

      // Enumerate speaker devices (labels depend on microphone permission)
      if (microphonePermissionStatus.value === "granted") {
        speakerDevices.value = await speaker.enumerateDevices();
      } else {
        speakerDevices.value = [];
      }

      // Load stored speaker preference
      const storedSpeakerId = speaker.getSelectedDeviceId(
        profile.value.user.accountId
      );
      if (storedSpeakerId) {
        // Verify the device still exists
        if (speakerDevices.value.some((d) => d.deviceId === storedSpeakerId)) {
          selectedSpeakerDeviceId.value = storedSpeakerId;
        }
      } else if (speakerDevices.value.length > 0) {
        // Use default if no preference stored
        selectedSpeakerDeviceId.value = speakerDevices.value[0].deviceId;
      }
    };

    const requestMicrophonePermission = async () => {
      try {
        await microphone.requestPermission();
        await refreshPermissions();
        await refreshDevices();
      } catch (error) {
        throw new Error("Failed to request microphone permission", {
          cause: error,
        });
      }
    };

    const onMicrophoneDeviceChange = (deviceId: string) => {
      selectedMicrophoneDeviceId.value = deviceId;
      microphone.setSelectedDeviceId(profile.value.user.accountId, deviceId);
    };

    const onSpeakerDeviceChange = (deviceId: string) => {
      selectedSpeakerDeviceId.value = deviceId;
      speaker.setSelectedDeviceId(profile.value.user.accountId, deviceId);
    };

    // Watch for device changes
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", refreshDevices);
    }

    onMounted(async () => {
      await refreshPermissions();
      await refreshDevices();
    });

    return {
      in_electron,
      in_mac,
      microphonePermissionStatus,
      screenRecordingPermissionStatus,
      speakerPermissionStatus,
      microphoneDevices,
      selectedMicrophoneDeviceId,
      speakerDevices,
      selectedSpeakerDeviceId,
      formatPermissionStatus,
      refreshPermissions,
      refreshDevices,
      requestMicrophonePermission,
      onMicrophoneDeviceChange,
      onSpeakerDeviceChange,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.permissions {
  display: flex;
  flex-direction: column;
  width: 100%;

  .permission {
    display: flex;
    flex-direction: column;
    padding: 24px 0;

    @include themed() {
      border-bottom: 1px solid t($grey-10);
    }

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      border-bottom: none;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
    }

    .content {
      h3 {
        margin: 0 0 8px 0;
        @extend %font-body-medium;
      }

      p {
        margin: 0;
        @extend %font-caption-regular;

        @include themed() {
          color: t($grey-60);
        }
      }
    }

    .device-select {
      margin-top: 16px;
    }
  }
}

.status {
  @extend %font-caption-regular;

  &.granted {
    @include themed() {
      color: t($tertiary-50);
    }
  }

  &.denied {
    @include themed() {
      color: t($secondary-50);
    }
  }

  &.not-determined,
  &.unknown,
  &.unsupported {
    @include themed() {
      color: t($grey-50);
    }
  }
}

.actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.help {
  @extend %font-caption-regular;
  margin-top: 4px;

  @include themed() {
    color: t($grey-50);
  }
}

:deep(.v-select.select) {
  @extend %font-caption-regular;
  max-width: 50%;

  .vs__dropdown-toggle {
    height: 100%;
    padding: 0;

    .vs__selected {
      background-color: transparent;
      height: 100%;
      border: none;
      border-radius: 0;
      margin: 0;
    }

    .vs__search {
      padding: 0;
    }

    .vs__actions {
      padding-inline-end: 12px;
    }
  }

  .vs__dropdown-menu {
    min-width: 0;
  }
}
</style>
